<template>
    <SubpageTitleContainer
        class="pt-0"
        title="EasyPOS Air"
        subText="“포스는 가볍게, 매장 운영은 자유롭게”<br>EasyPOS Air는 이지포스의 가벼운 버전으로 직관적인 화면 구성과 간편한 설정 기능을 갖춘 새로운 POS 솔루션입니다."
    />
    <SubSection type="wide" class="air-features">
        <SubTitle3 listStyle="dot">특징</SubTitle3>
        <div class="air-feature-list">
            <div v-for="feature in features" :key="feature.title">
                <h3>{{ feature.title }}</h3>
                <p>{{ feature.description }}</p>
            </div>
        </div>
    </SubSection>
    <SubSection>
        <StickyContainer title="주요 기능" secondTitle="" :leftItems="leftItems" :rightItems="rightItems" />
    </SubSection>
    <SubSection type="wide" class="air-details">
        <SubTitle3 listStyle="dot">이런 매장에 추천해요!</SubTitle3>
        <ul class="air-recommendations">
            <li>복잡한 설치 과정 없이 빠르고 간편하게 사용하고 싶은 매장</li>
            <li>직관적인 화면으로 매장 환경에 맞게 자유롭게 설정하고 싶은 매장</li>
            <li>별도 포스 장비 없이 태블릿·노트북으로 비용과 공간을 절약하고 싶은 매장</li>
            <li>소형 음식점·테이크아웃 카페 등 간편한 결제가 중요한 중소형 매장</li>
        </ul>
    </SubSection>
    <SubSection class="air-install">
        <SubTitle3 listStyle="dot">이지포스 에어 설치하기</SubTitle3>
        <div class="air-downloads">
            <div v-for="installer in installers" :key="installer.os" class="air-download-card">
                <h3>{{ installer.os }}</h3>
                <p>{{ installer.description }}</p>
                <span class="air-download-version">v1.0.1 · {{ installer.format }}</span>
                <a :href="`${downloadBase}${installer.filename}`" download :aria-label="`${installer.os} 버전 다운로드`">
                    다운로드
                    <i aria-hidden="true"></i>
                </a>
            </div>
        </div>
    </SubSection>
</template>

<script>
import SubpageTitleContainer from '@/components/SubpageTitleContainer.vue';
import SubSection from '@/components/SubSection.vue';
import SubTitle3 from '@/components/SubTitle3.vue';
import StickyContainer from '@/components/StickyContainer.vue';

const functions = [
    { title: '로그인 화면', description: '기존 EasyPOS 매장의 경우, 가맹점 로그인 후 바로 사용 가능<br>EasyPOS가 처음이라면, 체험판 가입 후 사용 가능' },
    { title: '상품 등록', description: '상품과 옵션을 POS에서 직접 추가하고 판매 여부까지 쉽게 관리 가능' },
    { title: '주문 화면 편집', description: '카테고리와 메뉴의 위치/순서/노출 방식을 직접 설정 가능' },
    { title: '결제 화면', description: '금액분할부터 더치페이, 메뉴별 결제까지 다양한 결제 방식 지원' },
    { title: '매출 관리 화면', description: '매출 현황을 원하는 방식으로 한눈에 확인 가능' },
    { title: '고객 관리', description: '고객 포인트 적립 및 사용 가능' },
    { title: '할인 관리', description: '요일·시간·상품별로 세분화된 할인 설정 가능' },
];

export default {
    components: { SubpageTitleContainer, SubSection, SubTitle3, StickyContainer },
    data() {
        return {
            downloadBase: `${process.env.BASE_URL}downloads/easypos-air/`,
            installers: [
                { os: 'Windows', description: 'Windows용 EasyPOS Air 설치 파일', format: 'ZIP', filename: 'EasyPOS_AIR_SETUP_V1.0.1_Windows.zip' },
                { os: 'Android', description: 'Android용 EasyPOS Air 설치 파일', format: 'APK', filename: 'EasyPOS_AIR_SETUP_V1.0.1_Android.apk' },
            ],
            features: [
                { title: '간편한 상품 등록 및 관리', description: 'POS에서 직접 카테고리, 상품, 옵션을 등록하고 수정할 수 있습니다.' },
                { title: '직관적인 사용자 인터페이스', description: '깔끔하고 직관적인 UI/UX로 원하는 기능을 쉽고 빠르게 찾을 수 있습니다.' },
                { title: '다양한 설치 환경 지원', description: 'POS 장비 없이도 포스 프로그램만 설치 받아 Windows/Android/iOS에서 사용할 수 있습니다.' },
            ],
            leftItems: functions.map((item, index) => ({ text: item.title, active: index === 0 })),
            rightItems: functions.map((item, index) => ({
                ...item,
                img: require(`@/assets/easypos-air/feature-${String(index + 1).padStart(2, '0')}.png`),
            })),
        };
    },
};
</script>

<style lang="scss" scoped>
.air-feature-list {
    border-top: 2px solid var(--black2);
    padding-top: 50px;
    display: grid;
    gap: 40px;

    h3 {
        font-size: 24px;
        font-weight: 700;
        margin-bottom: 16px;
    }

    p {
        font-size: 18px;
        line-height: 1.7;
        color: var(--black2);
        word-break: keep-all;
    }
}

.air-recommendations {
    border-top: 1px solid var(--border-color1);
    padding: 40px 0 0;

    li {
        position: relative;
        list-style: none;
        padding-left: 12px;
        color: var(--black3);
        font-family: 'Pretendard', sans-serif;
        font-weight: 400;
        letter-spacing: -0.025em;
        font-size: 18px;
        line-height: 1.8;
        word-break: keep-all;

        + li {
            margin-top: 10px;
        }

        &::after {
            position: absolute;
            content: '';
            width: 4px;
            height: 4px;
            background: #ccc;
            top: calc(0.9em - 2px);
            left: 0;
        }
    }
}

.air-downloads {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 26px;
}

.air-download-card {
    background: var(--gray2);
    padding: 60px 50px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    h3 {
        font-size: 28px;
        font-weight: 700;
        letter-spacing: -0.025em;
        color: var(--black1);
        margin-bottom: 20px;
    }

    p {
        font-size: 18px;
        line-height: 1.6;
        color: var(--black3);
        word-break: keep-all;
    }

    .air-download-version {
        margin-top: 12px;
        font-size: 14px;
        color: var(--black3);
    }

    a {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        min-height: 50px;
        margin-top: 36px;
        padding: 0 28px;
        border: 2px solid var(--red);
        border-radius: 25px;
        color: var(--red);
        font-size: 18px;
        font-weight: 500;
        transition: background-color 0.2s, color 0.2s;

        i {
            width: 24px;
            height: 24px;
            background: url('@/assets/ic_download.png') center no-repeat;
        }

        &:hover, &:focus-visible {
            background: var(--red);
            color: #fff;

            i { background-image: url('@/assets/ic_download-w.png'); }
        }
    }
}

@media screen and (max-width: 768px) {
    .air-feature-list {
        padding-top: 30px;
        gap: 30px;

        h3 { font-size: 20px; }
        p { font-size: 16px; }
    }

    .air-recommendations {
        padding-top: 30px;

        li { font-size: 16px; }
    }

    .air-downloads {
        grid-template-columns: 1fr;
        gap: 20px;
    }

    .air-download-card {
        padding: 32px 28px;

        h3 { font-size: 24px; }
        p { font-size: 16px; }

        a {
            min-height: 44px;
            margin-top: 28px;
            padding: 0 24px;
            font-size: 16px;
        }
    }
}
</style>
